import Component from '@glimmer/component';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { and, eq, not } from 'ember-truth-helpers';
import { variation } from 'ember-launch-darkly';
import can from 'ember-can/helpers/can';
import {
  HdsPageHeader,
  HdsButton,
  HdsBadge,
  HdsCardContainer,
  HdsTextDisplay,
  HdsTextBody,
  HdsLinkStandalone,
} from '@hashicorp/design-system-components/components';
import { CoreLayoutMain, BreadcrumbsContainer, Flex } from 'core/components';
import { boxPadding } from 'core/modifiers';
import { GEOS } from 'core/utils/constants';
import { ResourceType } from 'core/services/terraform-unification';
import TerraformUnification from 'hcp/components/hcp/alert/terraform-unification';
import OverviewCard from 'hcp/components/page/terraform/overview-card';

/**
 *
 * `PageTerraform` is the dashboard page for the Terraform route in HCP.
 *
 *
 * ```
 * <Page::Terraform
 *   @model={{this.model}}
 * />
 * ```
 *
 * @class PageTerraform
 *
 */
export default class PageTerraform extends Component {
  @service config;
  @service userContext;
  @service('terraform-unification') terraformUnification;

  GEOS = GEOS;

  get deployGeo() {
    return this.config.app?.deployGeo ?? GEOS.US;
  }

  get terraformUrl() {
    return this.terraformUnification.getDeploymentUrl({
      geo: this.deployGeo,
    });
  }

  get terraformOrganizationsUrl() {
    return this.terraformUnification.getResourceUrl({
      url: this.terraformUrl,
      resourceType: ResourceType.TerraformOrganizations,
      resourceIdentifiers: {},
    });
  }

  get terraformProjectsUrl() {
    const { organization, project } = this.args.model ?? {};

    if (!organization || !project) {
      return this.terraformUrl;
    }

    return this.terraformUnification.getResourceUrl({
      url: this.terraformUrl,
      resourceType: ResourceType.TerraformProject,
      resourceIdentifiers: {
        terraformOrganizationName: organization?.name,
        projectId: project?.id,
      },
    });
  }

  <template>
    <CoreLayoutMain>
      <:header>
        <HdsPageHeader as |PH|>
          <PH.Breadcrumb>
            <BreadcrumbsContainer />
          </PH.Breadcrumb>
          <PH.IconTile @icon="terraform" @color="terraform" />
          <PH.Title data-test-terraform-title>
            {{t "components.page.terraform.title"}}
          </PH.Title>
          {{#if this.userContext.organizationMeta.unifiedExperienceEnabled}}
            <PH.Actions>
              <HdsButton
                data-test-hcp-terraform-button
                @text={{t "components.page.terraform.action-link"}}
                @icon="external-link"
                @iconPosition="trailing"
                @href={{this.terraformProjectsUrl}}
                @size="medium"
              />
            </PH.Actions>
          {{else}}
            <PH.Actions>
              <HdsButton
                data-test-hcp-terraform-button
                @text={{t "components.page.terraform.go-to-hcp-terraform"}}
                @icon="external-link"
                @iconPosition="trailing"
                @href={{this.terraformOrganizationsUrl}}
                @size="medium"
              />
            </PH.Actions>
          {{/if}}
          <PH.Description>
            {{t "components.page.terraform.description"}}
          </PH.Description>
        </HdsPageHeader>
      </:header>
      <:alert>
      </:alert>
      <:body>
        <div class="terraform">
          {{#if this.userContext.organizationMeta.unifiedExperienceEnabled}}
            <TerraformUnification
              @icon={{false}}
              data-test-hcp-terraform-integrated-banner
              as |A|
            >
              <A.Title data-test-hcp-terraform-banner-title>
                {{t
                  "components.page.terraform.terraform-unification-alert.introducing-hcp-terraform-integrated"
                }}
              </A.Title>
              <A.Description>
                {{t
                  "components.page.terraform.terraform-unification-alert.manage-and-administer-terraform"
                }}
              </A.Description>
              <A.LinkStandalone
                data-test-hcp-terraform-banner-link
                @href="https://developer.hashicorp.com/terraform/cloud-docs/users-teams-organizations/organizations"
                @icon="docs-link"
                @iconPosition="trailing"
                @size="medium"
                @text={{t
                  "components.page.terraform.terraform-unification-alert.learn-more-about-integrated-organizations"
                }}
              />
            </TerraformUnification>
          {{else}}
            {{#if (variation "hcp-ui-terraform-standalone-integration-alert")}}
              {{#if
                (can
                  "create organizations"
                  @model.organizations
                  userId=@model.user.id
                  isSsoEnabled=@model.isSsoEnabled
                )
              }}
                <TerraformUnification
                  @icon={{false}}
                  data-test-hcp-terraform-standalone-banner
                  as |A|
                >
                  <A.Title data-test-hcp-terraform-banner-title>
                    {{t
                      "components.page.terraform.terraform-unification-alert.introducing-hcp-terraform-integrated"
                    }}
                  </A.Title>
                  <A.Description>
                    {{t
                      "components.page.terraform.terraform-unification-alert.you-can-now-manage-infra-security-in-hcp"
                    }}
                    <br />
                    <br />
                    <HdsBadge
                      @text={{t
                        "components.page.terraform.terraform-unification-alert.note"
                      }}
                    />
                    {{t
                      "components.page.terraform.terraform-unification-alert.org-name-is-standalone-not-eligible"
                      organizationName=@model.organization.name
                    }}
                  </A.Description>
                  <A.LinkStandalone
                    @icon="plus"
                    @iconPosition="leading"
                    @route="cloud.orgs.create"
                    @size="medium"
                    @text={{t
                      "components.page.terraform.terraform-unification-alert.create-a-new-organization"
                    }}
                    data-test-hcp-terraform-banner-link
                  />
                </TerraformUnification>
              {{/if}}
            {{/if}}
          {{/if}}
          {{#if
            (and
              (eq this.deployGeo this.GEOS.US)
              (not this.userContext.organizationMeta.unifiedExperienceEnabled)
            )
          }}
            <div class="row row--2-1">
              <HdsCardContainer
                @hasBorder="true"
                @level="mid"
                class="terraform__card terraform__card--hvs"
                {{boxPadding "lg"}}
                data-test-terraform-better-together-card
              >
                <Flex @direction="column" @justifyContent="space-between">
                  <Flex @alignItems="start" @direction="column" @gap="md">
                    <Flex @alignItems="start" @direction="column" @gap="xs">
                      <HdsBadge
                        @color="highlight"
                        @text={{t
                          "components.page.terraform.cards.secrets.badge"
                        }}
                        @type="outlined"
                      />
                      <HdsTextDisplay @color="strong" @size="300" @tag="h2">
                        {{t "components.page.terraform.cards.secrets.title"}}
                      </HdsTextDisplay>
                    </Flex>
                    <HdsTextBody @color="primary">
                      {{t
                        (if
                          (variation "hcp-rebrand-milestone-0")
                          "components.page.terraform.cards.secrets.content"
                          "components.page.terraform.cards.secrets.content-old"
                        )
                        htmlSafe=true
                      }}
                    </HdsTextBody>
                  </Flex>
                  <HdsLinkStandalone
                    @icon="arrow-right"
                    @iconPosition="trailing"
                    @isHrefExternal={{true}}
                    @href={{this.terraformOrganizationsUrl}}
                    @text={{t
                      "components.page.terraform.cards.secrets.link-text"
                    }}
                    data-test-hvs-tfc-card-link
                  />
                </Flex>
              </HdsCardContainer>
              <HdsCardContainer
                @hasBorder="true"
                @level="mid"
                class="terraform__card"
                {{boxPadding "lg"}}
                data-test-terraform-provider-card
              >
                <Flex
                  @direction="column"
                  @gap="md"
                  @justifyContent="space-between"
                >
                  <Flex @alignItems="start" @direction="column" @gap="lg">
                    <Flex @alignItems="start" @direction="column" @gap="xs">
                      <HdsBadge
                        @text={{t
                          "components.page.terraform.cards.provider.heading"
                        }}
                      />
                      <HdsTextDisplay @color="primary" @size="300" @tag="h2">
                        {{t "components.page.terraform.cards.provider.title"}}
                      </HdsTextDisplay>
                    </Flex>
                    <HdsTextBody @color="primary" @size="200">
                      {{t "components.page.terraform.cards.provider.content"}}
                    </HdsTextBody>
                  </Flex>
                  <HdsLinkStandalone
                    @icon="docs-link"
                    @href="https://registry.terraform.io/providers/hashicorp/hcp/latest"
                    @iconPosition="trailing"
                    @text={{t "components.page.terraform.cards.provider.link"}}
                  />
                </Flex>
              </HdsCardContainer>
            </div>
            <div class="row row--1-2">
              <HdsCardContainer
                @hasBorder="true"
                @level="mid"
                class="terraform__card"
                {{boxPadding "lg"}}
                data-test-terraform-changelog-card
              >
                <Flex
                  @direction="column"
                  @gap="md"
                  @justifyContent="space-between"
                >
                  <Flex @direction="column" @gap="md">
                    <HdsTextDisplay @color="primary" @size="300" @tag="h2">
                      {{t "components.page.terraform.cards.changelog.title"}}
                    </HdsTextDisplay>
                    <HdsTextBody @color="primary" @size="200">
                      {{t "components.page.terraform.cards.changelog.content"}}
                    </HdsTextBody>
                  </Flex>
                  <HdsLinkStandalone
                    @href="https://github.com/hashicorp/terraform-provider-hcp/blob/main/CHANGELOG.md"
                    @icon="docs-link"
                    @iconPosition="trailing"
                    @text={{t "components.page.terraform.cards.changelog.link"}}
                  />
                </Flex>
              </HdsCardContainer>
              <HdsCardContainer
                @level="mid"
                @hasBorder="true"
                class="terraform__card"
                {{boxPadding "lg"}}
                data-test-terraform-resources-card
              >
                <Flex alignItems="start" @direction="column" @gap="md">
                  <HdsTextDisplay @color="primary" @size="300" @tag="h2">
                    {{t "components.page.terraform.cards.resources.title"}}
                  </HdsTextDisplay>
                  <div class="terraform__provider-guides--body">
                    <div>
                      <HdsLinkStandalone
                        @href="https://learn.hashicorp.com/tutorials/cloud/terraform-hcp-provider-vault"
                        @icon="learn-link"
                        @iconPosition="trailing"
                        @text={{t
                          "components.page.terraform.cards.resources.links.vault.title"
                        }}
                      />
                      <HdsTextBody @color="primary" @size="200">
                        {{t
                          "components.page.terraform.cards.resources.links.vault.content"
                        }}
                      </HdsTextBody>
                    </div>
                    <div>
                      <HdsLinkStandalone
                        @href="https://learn.hashicorp.com/tutorials/cloud/terraform-hcp-consul-provider"
                        @icon="learn-link"
                        @iconPosition="trailing"
                        @text={{t
                          "components.page.terraform.cards.resources.links.consul.title"
                        }}
                      />
                      <HdsTextBody @color="primary" @size="200">
                        {{t
                          "components.page.terraform.cards.resources.links.consul.content"
                        }}
                      </HdsTextBody>
                    </div>
                    <div>
                      <HdsLinkStandalone
                        @href="https://registry.terraform.io/providers/hashicorp/hcp/latest/docs/guides/peering"
                        @icon="learn-link"
                        @iconPosition="trailing"
                        @text={{t
                          "components.page.terraform.cards.resources.links.peer-vpc.title"
                        }}
                      />
                      <HdsTextBody @color="primary" @size="200">
                        {{t
                          "components.page.terraform.cards.resources.links.peer-vpc.content"
                        }}
                      </HdsTextBody>
                    </div>
                    <div>
                      <HdsLinkStandalone
                        @href="https://registry.terraform.io/providers/hashicorp/hcp/latest/docs/resources/aws_transit_gateway_attachment"
                        @icon="learn-link"
                        @iconPosition="trailing"
                        @text={{t
                          "components.page.terraform.cards.resources.links.tg-hvn.title"
                        }}
                      />
                      <HdsTextBody @color="primary" @size="200">
                        {{t
                          "components.page.terraform.cards.resources.links.tg-hvn.content"
                        }}
                      </HdsTextBody>
                    </div>
                    <div>
                      <HdsLinkStandalone
                        @href="https://registry.terraform.io/providers/hashicorp/hcp/latest/docs"
                        @icon="docs-link"
                        @iconPosition="trailing"
                        @text={{t
                          "components.page.terraform.cards.resources.links.docs"
                        }}
                      />
                    </div>
                  </div>
                </Flex>
              </HdsCardContainer>
            </div>
          {{else}}
            <div class="row row--1-1-1">
              <OverviewCard
                @cardContext="organizationManagement"
                @orgId={{this.userContext.organization.id}}
              />
              <OverviewCard
                @cardContext="sso"
                @orgId={{this.userContext.organization.id}}
              />
              <OverviewCard
                @cardContext="rbac"
                @orgId={{this.userContext.organization.id}}
              />
            </div>
            <HdsCardContainer
              @hasBorder="true"
              @level="mid"
              class="terraform__card"
              {{boxPadding "md"}}
            >
              <Flex
                @direction="column"
                @gap="md"
                @justifyContent="space-between"
              >
                <Flex @alignItems="start" @direction="column" @gap="md">
                  <Flex @alignItems="start" @direction="column" @gap="xs">
                    <HdsTextDisplay @color="primary" @size="300" @tag="h2">
                      {{t "components.page.terraform.cards.provider.title"}}
                    </HdsTextDisplay>
                  </Flex>
                  <HdsTextBody @color="primary" @size="200">
                    {{t "components.page.terraform.cards.provider.content"}}
                  </HdsTextBody>
                </Flex>
                <HdsLinkStandalone
                  @icon="docs-link"
                  @href="https://registry.terraform.io/providers/hashicorp/hcp/latest"
                  @iconPosition="trailing"
                  @text={{t "components.page.terraform.cards.provider.link"}}
                />
              </Flex>
            </HdsCardContainer>
          {{/if}}
        </div>
      </:body>
    </CoreLayoutMain>
  </template>
}
